import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export type ConfirmDialogTone = "destructive" | "warning" | "info";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;

  title: string;

  /**
   * Coloured callout below the header. Pass `null` to hide it entirely
   * for trivial confirms.
   */
  tone?: ConfirmDialogTone;
  warningTitle?: string;
  warningDescription?: ReactNode;
  hideCallout?: boolean;

  confirmLabel?: string;
  loadingLabel?: string;
  cancelLabel?: string;

  isLoading?: boolean;
  disabled?: boolean;

  size?: "sm" | "md" | "lg" | "xl";

  /** Body content rendered between the callout and the footer. */
  children?: ReactNode;
}

const SIZE_CLASS: Record<NonNullable<ConfirmDialogProps["size"]>, string> = {
  sm: "sm:max-w-[420px]",
  md: "sm:max-w-[500px]",
  lg: "sm:max-w-[600px]",
  xl: "sm:max-w-[680px]",
};

const TONE_STYLES: Record<
  ConfirmDialogTone,
  {
    box: string;
    icon: string;
    iconNode: ReactNode;
    title: string;
    body: string;
    button: string;
  }
> = {
  destructive: {
    box: "bg-red-50 border-red-200",
    icon: "text-red-600",
    iconNode: <AlertTriangle size={18} />,
    title: "text-red-800",
    body: "text-red-700",
    button: "bg-red-600 hover:bg-red-700 text-white",
  },
  warning: {
    box: "bg-amber-50 border-amber-200",
    icon: "text-amber-600",
    iconNode: <AlertTriangle size={18} />,
    title: "text-amber-900",
    body: "text-amber-800",
    button: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  info: {
    box: "bg-blue-50 border-blue-200",
    icon: "text-blue-600",
    iconNode: <Info size={18} />,
    title: "text-blue-900",
    body: "text-blue-800",
    button:
      "bg-[var(--color-success,#156548)] hover:bg-[var(--color-success,#156548)]/90 text-white",
  },
};

/**
 * Project-wide confirm modal. Mirrors the visual language of the original
 * Delete Stage modal — dialog with a header + separator, a coloured warning
 * callout, optional body content, and a Cancel + tone-coloured confirm button
 * footer. Use this for every destructive / blocking confirm flow so the UX
 * stays consistent.
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  tone = "destructive",
  warningTitle = "This action cannot be undone",
  warningDescription,
  hideCallout = false,
  confirmLabel = "Delete",
  loadingLabel,
  cancelLabel = "Cancel",
  isLoading = false,
  disabled = false,
  size = "md",
  children,
}: ConfirmDialogProps) {
  const t = TONE_STYLES[tone];

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isLoading && onClose()}>
      <DialogContent className={cn(SIZE_CLASS[size], "rounded-xl")}>
        <DialogHeader>
          <p className="text-[14px] font-semibold tracking-[-0.28px] text-[#051912]">
            {title}
          </p>
          <Separator />
          {!hideCallout && warningDescription && (
            <DialogDescription className="text-black-7 pt-2" asChild>
              <div>
                <div
                  className={cn(
                    "flex items-start gap-3 p-3 border rounded-lg mb-1",
                    t.box
                  )}
                  role="alert"
                >
                  <div className={cn("shrink-0 mt-0.5", t.icon)}>
                    {t.iconNode}
                  </div>
                  <div className={cn("text-sm", t.title)}>
                    {warningTitle && (
                      <p className="font-medium mb-1">{warningTitle}</p>
                    )}
                    <div
                      className={cn(
                        "text-xs leading-relaxed",
                        t.body
                      )}
                    >
                      {warningDescription}
                    </div>
                  </div>
                </div>
              </div>
            </DialogDescription>
          )}
        </DialogHeader>

        {children && (
          <div className="flex flex-col gap-3 pt-1">{children}</div>
        )}

        <DialogFooter className="mt-6 flex justify-end gap-2">
          <Button variant="cancel" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm()}
            disabled={disabled || isLoading}
            className={t.button}
          >
            {isLoading ? loadingLabel ?? `${confirmLabel}…` : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
